import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../hooks/useGlobalSettings';

const resetSchema = z.object({
  newPassword: z
    .string()
    .min(8, 'A senha deve ter pelo menos 8 caracteres'),
  confirmPassword: z
    .string()
    .min(1, 'Confirme sua nova senha'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

type ResetFormData = z.infer<typeof resetSchema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [apiMessage, setApiMessage] = useState('');
  const { resetPassword } = useAuth();
  const { settings } = useGlobalSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const onSubmit = async (data: ResetFormData) => {
    if (!token) return;
    setIsSubmitting(true);
    try {
      const result = await resetPassword(token, data.newPassword, data.confirmPassword);
      if (result.success) {
        setResetDone(true);
        setApiMessage(result.message);
      } else {
        setApiMessage(result.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-4 font-oswald">
        <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl w-full max-w-md overflow-hidden">
          <div className="p-12 text-center space-y-6">
            <div className="w-16 h-16 bg-red-500/20 rounded-full mx-auto flex items-center justify-center">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2 uppercase tracking-tight">Link Inválido</h2>
            <p className="text-gray-400 text-sm">O link de redefinição de senha é inválido ou está ausente.</p>
            <Link
              to="/esqueci-senha"
              className="inline-block w-full py-4 bg-blue-600 text-white rounded-2xl font-bold text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-400/50 text-center"
            >
              Solicitar Novo Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-4 font-oswald">
      <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-12">
          <div className="mb-10 text-center">
            <div className="w-20 h-20 bg-white/10 rounded-[1.5rem] mx-auto mb-6 flex items-center justify-center backdrop-blur-xl border border-white/20 shadow-2xl">
              {settings?.logoUrl ? (
                <img src={settings.logoUrl} alt="Logo" className="w-14 h-14 object-contain" />
              ) : (
                <img src="/favicon.png" alt="Logo" className="w-14 h-14 object-contain" />
              )}
            </div>
            <h2 className="text-3xl font-bold text-white mb-2 uppercase tracking-tight">
              {resetDone ? 'Senha Redefinida' : 'Nova Senha'}
            </h2>
            <p className="text-gray-400 text-xs uppercase tracking-[0.2em] font-medium">
              {resetDone
                ? 'Sua senha foi alterada com sucesso'
                : 'Digite e confirme sua nova senha'}
            </p>
          </div>

          {resetDone ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full mx-auto flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">{apiMessage}</p>
              <Link
                to="/login"
                className="inline-block w-full py-4 bg-blue-600 text-white rounded-2xl font-bold text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-400/50 text-center"
              >
                Fazer Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="newPassword" className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] ml-2">
                  Nova Senha
                </label>
                <div className="relative group">
                  <input
                    {...register('newPassword')}
                    type="password"
                    id="newPassword"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                    placeholder="Mínimo 8 caracteres"
                    disabled={isSubmitting}
                  />
                </div>
                {errors.newPassword && (
                  <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.newPassword.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] ml-2">
                  Confirmar Nova Senha
                </label>
                <div className="relative group">
                  <input
                    {...register('confirmPassword')}
                    type="password"
                    id="confirmPassword"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                    placeholder="Repita a nova senha"
                    disabled={isSubmitting}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.confirmPassword.message}</p>
                )}
              </div>

              {apiMessage && (
                <p className="text-yellow-400 text-[10px] uppercase font-bold text-center">{apiMessage}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-400/50 relative overflow-hidden group/btn disabled:opacity-50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div>
                    Redefinindo...
                  </div>
                ) : (
                  'Redefinir Senha'
                )}
              </button>
            </form>
          )}

          <div className="mt-8 text-center">
            <p className="text-gray-500 text-[9px] uppercase tracking-[0.3em] font-bold">
              © {new Date().getFullYear()} {settings?.companyName || 'BChat'} • Todos os direitos reservados
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
